import React, { useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import { createPortal } from "react-dom";

import {
    AutocompleteDropdown,
    AutocompleteDropdownItem,
    CheckboxInput,
    ContentWrapper,
    DropdownContainer,
    ErrorMessage,
    FormRow,
    FormTitle,
    FormWrapper,
    Input,
    InputContainer,
    InputWrapper,
    Label,
    MultiSelectWrapper,
    OptionItem,
    OptionLabel,
    OptionsContainer,
    RemoveButton,
    RequiredAsterisk,
    SelectedItem,
    ToggleContainer,
    ToggleInput,
    ToggleSlider,
    ToggleText,
    ToggleWrapper,
} from "./Styled";

import { color } from "../../styles";
import DropdownMenu from "../DropdownMenu.tsx";
import { formatInternationalNumber } from "../../../utils/format";
import { validate } from "../../../utils/validation";
import { useContext } from "react";
import { ValidationErrorsContext } from "./ValidationErrorsContext";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import Calendar from "../../svg/Calendar";
import { api } from "../../hooks/api";
import PageLoader from "../LoadingPage";
import { ProductCategoryGroup } from "../../../data/entities";
import DatePickerWithTime from "../DatePickerWithTime";
import TextAndDropdownWithSearch, { TextAndDropdownOption } from "../TextAndDropdownWithSearch";
import { parseNumericInput } from "./numericInput";

export interface FieldConfigs {
    label: string;
    key: string;
    type: "toggle" | "text" | "dropdown" | "calendar" | "custom" | "datetime" | "quickFills" | "multiSelect" | "search" | "textanddropdown" | "autocomplete";
    required?: boolean;
    disabled?: boolean;
    options?: TextAndDropdownOption[];
    isInteger?: boolean;
    hideToggleText?: boolean;
    validation?: string;
    showPlaceholder?: boolean;
    numeric?: boolean;
    dataApi?: string;
    fetchItems?: (search: string) => Promise<any[]>;
    placeholder?: string;
    customComponent?: React.ReactNode;
    customProps?: any;
    noDataText?: string;
    onChange?: (value: any) => void; // Add custom onChange handler
    showAll?: boolean; // Flag to show all items for search type
    getOptionLabel?: (option: any) => string; // For autocomplete
    getOptionValue?: (option: any) => string | number; // For autocomplete
}

function ToggleType(props: {
    formData: Record<string, any>;
    field: FieldConfigs;
    onChange: () => void;
}) {
    return (
        <ToggleWrapper>
            <ToggleContainer>
                <ToggleInput
                    type="checkbox"
                    checked={props.formData[props.field.key]}
                    onChange={props.onChange}
                    disabled={props.field.disabled}
                />
                <ToggleSlider disabled={props.field.disabled} />
            </ToggleContainer>
            {!props.field.hideToggleText && (
                <ToggleText>
                    {props.formData[props.field.key]
                        ? "Hoạt động"
                        : "Không hoạt động"}
                </ToggleText>
            )}
        </ToggleWrapper>
    );
}

function TextType(props: {
    formData: Record<string, any>;
    field: FieldConfigs;
    onChange: (e: any) => void;
    onBlur: () => void;
    touched: Record<string, boolean>;
    errors: Record<string, string>;
}) {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = props.field.key.includes("password");

    const handleChange = (e: any) => {
        const value = e.target.value;

        if (props.field.type === "datetime") {
            const parsedDate = dayjs(value, "YYYY-MM-DDTHH:mm:ssZ", true);
            if (parsedDate.isValid() || value === "") {
                props.onChange({
                    target: {
                        value: value === "" ? "" : parsedDate.toISOString(),
                    },
                });
            }
        } else if (props.field.numeric) {
            const parsedInput = parseNumericInput(value);

            if (parsedInput.isValid) {
                props.onChange({
                    target: {
                        value: parsedInput.value,
                    },
                });
            }
        } else {
            props.onChange(e);
        }
    };

    const handleBlur = (e: any) => {
        if (props.field.numeric && props.formData[props.field.key]) {
            const num = parseFloat(props.formData[props.field.key].toString());
            if (!isNaN(num)) {
                props.onChange({
                    target: {
                        value: num,
                    },
                });
            }
        }
        props.onBlur();
    };

    const displayValue = props.field.numeric && props.formData[props.field.key]
        ? formatInternationalNumber(parseFloat(props.formData[props.field.key]))
        : props.formData[props.field.key];

    return (
        <div style={{ position: "relative", width: "100%" }}>
            <Input
                aria-label={props.field.label}
                placeholder="Vui lòng nhập"
                type={isPassword && !showPassword ? "password" : "text"}
                disabled={props.field.disabled}
                value={displayValue}
                onChange={handleChange}
                onBlur={handleBlur}
                isError={
                    !!(
                        props.touched[props.field.key] &&
                        props.errors[props.field.key]
                    )
                }
            />
            {isPassword && (
                <div
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                    }}
                >
                    {showPassword ? (
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z"
                                fill="#757575"
                            />
                        </svg>
                    ) : (
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5ZM12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7ZM12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15Z"
                                fill="#757575"
                            />
                        </svg>
                    )}
                </div>
            )}
        </div>
    );
}

export function CalendarType(props: {
    formData: Record<string, any>;
    field: FieldConfigs;
    onChange: (value: any) => void;
    onBlur: () => void;
    touched: Record<string, boolean>;
    errors: Record<string, string>;
}) {
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
            <DemoContainer
                components={["DatePicker"]}
                sx={{
                    paddingTop: "0px",
                    width: "100%",
                    "& .MuiTextField-root": {
                        width: "100%",
                    },
                }}
            >
                <DatePicker
                    value={
                        props.formData[props.field.key]
                            ? dayjs(props.formData[props.field.key])
                            : null
                    }
                    onChange={(newValue) => {
                        props.onChange(
                            newValue ? newValue.format("YYYY-MM-DD") : null,
                        );
                    }}
                    format="DD/MM/YYYY"
                    slotProps={{
                        textField: {
                            placeholder: "DD/MM/YYYY",
                            inputProps: { "aria-label": props.field.label },
                        },
                        popper: {
                            placement: "bottom-end",
                            sx: {
                                "& .MuiPaper-root": {
                                    marginTop: "4px",
                                },
                                "& .MuiPickersDay-root.Mui-selected": {
                                    backgroundColor: "#0058A0",
                                    "&:hover": {
                                        backgroundColor: "#0058A0",
                                    },
                                },
                                "& .MuiDayCalendar-weekDayLabel": {
                                    color: "#333739",
                                },
                            },
                        },
                    }}
                    dayOfWeekFormatter={(day) => {
                        const weekdays = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
                        return weekdays[day.day()];
                    }}
                    sx={{
                        width: "100%",
                        maxWidth: "655px",
                        "& .MuiInputBase-root": {
                            height: "40px",
                            fontSize: "14px",
                            fontStyle: "normal",
                            fontWeight: 400,
                            lineHeight: "normal",
                            fontFamily: "Noto Sans",
                            padding: "8px 12px",
                        },
                        "& .MuiInputBase-input": {
                            padding: "0px",
                            height: "22px",
                            "&::placeholder": {
                                color: "#757575",
                                opacity: 1,
                            },
                        },
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                                border:
                                    props.touched[props.field.key] &&
                                    props.errors[props.field.key]
                                        ? "1px solid #ff0000"
                                        : `1px solid ${color.emphasis.NEUTRAL_N90}`,
                                borderRadius: "5px",
                            },
                            "&:hover fieldset": {
                                border:
                                    props.touched[props.field.key] &&
                                    props.errors[props.field.key]
                                        ? "1px solid #ff0000"
                                        : `1px solid ${color.emphasis.NEUTRAL_N90}`,
                            },
                            "&.Mui-focused fieldset": {
                                border:
                                    props.touched[props.field.key] &&
                                    props.errors[props.field.key]
                                        ? "1px solid #ff0000"
                                        : `1px solid ${color.emphasis.NEUTRAL_N90}`,
                            },
                        },
                    }}
                    slots={{
                        openPickerIcon: Calendar,
                    }}
                    disabled={props.field.disabled}
                />
            </DemoContainer>
        </LocalizationProvider>
    );
}

const QuickFillsType = ({
                            dataApi,
                            placeholder,
                            onChange,
                            defaultItem,
                            disabled,
                        }: {
    dataApi: string;
    placeholder: string;
    onChange: any;
    defaultItem?: any;
    disabled?: boolean;
}) => {
    const [{ data, isLoading }] = api.get(dataApi);
    const [selectedItem, setSelectedItem] = useState<ProductCategoryGroup | null>(defaultItem);

    useEffect(() => {
        if (defaultItem) {
            setSelectedItem(defaultItem);
        }
    }, [defaultItem]);

    if (isLoading) return <PageLoader />;

    const items = data ? data.results : [];

    return (
        <DropdownMenu
            menuList={items.map((item: any) => ({ ...item, text: item.name }))}
            menuText={selectedItem ? selectedItem.name : placeholder}
            handleOnclickItem={(item: any) => {
                setSelectedItem(item);
                onChange(item);
            }}
            disabled={disabled}
            style={{
                flex: 1,
                width: "100%",
                height: "36px",
                maxWidth: "655px",
                padding: "6px 10px",
                border: `1px solid ${color.emphasis.NEUTRAL_N70}`,
                borderRadius: "5px",
                lineHeight: "24px",
                fontWeight: "400",
                fontSize: "14px",
                appearance: "none",
                color: selectedItem ? "inherit" : color.secondary.DARK_GREY,
            }}
        />
    );
};

function MultiSelectType(props: {
    formData: Record<string, any>;
    field: FieldConfigs;
    onChange: (value: any) => void;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const selectedValues = (props.formData[props.field.key] || "").split(";").filter(Boolean);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleToggleOption = (value: string) => {
        let newValues: string[];
        if (selectedValues.includes(value)) {
            newValues = selectedValues.filter((v: string) => v !== value);
        } else {
            newValues = [...selectedValues, value];
        }
        props.onChange(newValues.join(";"));
    };

    return (
        <MultiSelectWrapper ref={wrapperRef}>
            <DropdownContainer onClick={() => setIsOpen(!isOpen)}>
                {selectedValues.length > 0 ? (
                    selectedValues.map((value: string) => (
                        <SelectedItem key={value}>
                            {props.field.options?.find(opt => opt.value === value)?.text || value}
                            <RemoveButton
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    handleToggleOption(value);
                                }}
                            >
                                ×
                            </RemoveButton>
                        </SelectedItem>
                    ))
                ) : (
                    <span className="placeholder">Vui lòng chọn</span>
                )}
            </DropdownContainer>
            {isOpen && (
                <OptionsContainer>
                    {props.field.options?.map(option => (
                        <OptionItem
                            key={option.value}
                            onClick={() => handleToggleOption(option.value)}
                        >
                            <CheckboxInput
                                type="checkbox"
                                id={option.value}
                                checked={selectedValues.includes(option.value)}
                                onChange={(e) => e.stopPropagation()} // Prevent double triggering
                            />
                            <OptionLabel
                                htmlFor={option.value}
                                onClick={(e) => e.preventDefault()} // Prevent label from triggering checkbox directly
                            >
                                {option.text}
                            </OptionLabel>
                        </OptionItem>
                    ))}
                </OptionsContainer>
            )}
        </MultiSelectWrapper>
    );
}

function SearchDropdown(props: {
    field: FieldConfigs;
    value: any;
    onChange: (value: any) => void;
}) {
    const [search, setSearch] = useState("");
    const [menuList, setMenuList] = useState<any[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const initialFetchDoneRef = useRef<boolean>(false);
    const hasSearchedRef = useRef<boolean>(false);

    // Keep track of the most recently selected value
    const [selectedValue, setSelectedValue] = useState<any>(props.value);

    // Initialize the search text from props.value if it exists
    useEffect(() => {
        if (props.value) {
            if (typeof props.value === "object" && props.value !== null) {
                // If we have an object, display its name
                const displayName = props.value.name || props.value.display_name || "";
                setSearch(displayName);
                setSelectedValue(props.value);
            } else if (typeof props.value === "string") {
                // If we have a string ID, we'll try to load the full object later
                setSelectedValue(props.value);
            }
        }
    }, [props.value]);

    // Disable automatic query execution by passing true as the third parameter
    const [{ data, isLoading: apiLoading }, makeRequest] = api.get(props.field.dataApi || "", undefined, true);

    // Load initial data only once when needed
    useEffect(() => {
        if (props.field.showAll && !initialFetchDoneRef.current) {
            initialFetchDoneRef.current = true;
            makeRequest({ page_size: 100 });
        }
    }, [props.field.showAll, makeRequest]);

    const debouncedFetch = useRef(
        debounce((searchQuery: string) => {
            // Handle empty search case
            if (!searchQuery.trim()) {
                if (props.field.showAll) {
                    // Fetch all results again for showAll when search is cleared
                    makeRequest({ page_size: 100 });
                } else {
                    // For regular search fields, just clear results
                    setMenuList([]);
                }
                return;
            }

            // Normal search with query
            hasSearchedRef.current = true;
            makeRequest({
                search: searchQuery,
                page_size: props.field.showAll ? 100 : 5,
            });
        }, 300),
    ).current;

    // Handle search changes
    useEffect(() => {
        // Skip initial empty search only on first render
        if (props.field.showAll && search === "" && !hasSearchedRef.current) {
            return;
        }
        debouncedFetch(search);
        return () => debouncedFetch.cancel();
    }, [search, props.field.showAll]);

    // Update menu list when data changes and try to match string IDs
    useEffect(() => {
        if (data?.results) {
            setMenuList(data.results);

            // If we have a string ID, try to find the matching object
            if (selectedValue && typeof selectedValue === "string") {
                const foundItem = data.results.find((item: any) => item.id === selectedValue);
                if (foundItem) {
                    // Update the display text
                    setSearch(foundItem.name || foundItem.display_name || "");
                    // Update the form value with the full object
                    setSelectedValue(foundItem);
                    props.onChange(foundItem);
                }
            }
        }
    }, [data, selectedValue, props]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Function to handle selection of an item
    const handleSelectItem = (option: any) => {
        // Save the selected value
        setSelectedValue(option);
        // Update the displayed text
        setSearch(option.name || option.display_name || "");
        // Close the dropdown
        setIsOpen(false);
        // Notify parent component
        if (props.field.onChange) {
            props.field.onChange(option);
        } else {
            props.onChange(option);
        }
    };

    return (
        <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
            <Input
                placeholder="Tìm kiếm..."
                value={search}
                onChange={(e) => {
                    setSearch(e.target.value);
                    setIsOpen(true);
                }}
                onFocus={() => {
                    setIsOpen(true);
                    // If showAll is true and we haven't loaded data yet, fetch on focus
                    if (props.field.showAll && menuList.length === 0 && !apiLoading) {
                        makeRequest({ page_size: 100 });
                    }
                }}
            />
            {isOpen && (
                <div style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    maxHeight: "200px",
                    overflowY: "auto",
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    zIndex: 1000,
                    marginTop: "4px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}>
                    {apiLoading ? (
                        <div style={{ padding: "8px", textAlign: "center" }}>Đang tìm kiếm...</div>
                    ) : menuList.length > 0 ? (
                        menuList.map((option: any) => (
                            <div
                                key={option.id}
                                onClick={() => handleSelectItem(option)}
                                style={{
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                    borderBottom: "1px solid #f0f0f0",
                                    backgroundColor: "white",
                                    transition: "background-color 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "#f5f5f5";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "white";
                                }}
                            >
                                {option.customer_code ?
                                    `[${option.customer_code}] ${option.name}` :
                                    option.name || option.display_name}
                            </div>
                        ))
                    ) : (
                        props.field.showAll ?
                            <div style={{ padding: "8px", textAlign: "center" }}>
                                Không có dữ liệu
                            </div> :
                            <div style={{ padding: "8px", textAlign: "center" }}>
                                {props.field.noDataText || "Không tìm thấy kết quả"}
                            </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Autocomplete(props: {
    field: FieldConfigs;
    value: any;
    onChange: (value: any) => void;
}) {
    const [search, setSearch] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [dropdownStyle, setDropdownStyle] = useState<any>({});

    const menuList = useMemo(() => props.field.options || [], [props.field.options]);

    const getLabel = props.field.getOptionLabel || ((o: any) => o?.text || "");
    const getValue = props.field.getOptionValue || ((o: any) => o?.value ?? o?.id);

    const filteredMenuList = useMemo(() => {
        if (!search.trim()) return menuList;
        const searchLower = search.toLowerCase();
        return menuList.filter((item: any) =>
            getLabel(item).toLowerCase().includes(searchLower)
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, menuList]);

    // Sync value from FE → input text
    useEffect(() => {
        if (!props.value) return setSearch("");
        if (typeof props.value === "object") setSearch(getLabel(props.value));
        else {
            const found = menuList.find((o: any) => getValue(o) === props.value);
            if (found) setSearch(getLabel(found));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.value, menuList]);

    // Close on click outside
    useEffect(() => {
        const handleOutside = (e: MouseEvent) => {
            if (
                inputRef.current &&
                !inputRef.current.contains(e.target as Node) &&
                !(e.target as HTMLElement).closest(".autocomplete-dropdown")
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, []);

    const openDropdown = () => {
        if (!inputRef.current) return;

        const rect = inputRef.current.getBoundingClientRect();

        setDropdownStyle({
            position: "absolute",
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
            zIndex: 9999,
        });

        setIsOpen(true);
    };

    const handleSelect = (option: any) => {
        setSearch(getLabel(option));
        setIsOpen(false);

        props.field.onChange
            ? props.field.onChange(option)
            : props.onChange(option);
    };

    return (
        <>
            <Input
                ref={inputRef}
                placeholder="Tìm kiếm..."
                value={search}
                onFocus={openDropdown}
                onChange={(e) => {
                    const txt = e.target.value;
                    setSearch(txt);
                    openDropdown();
                    if (!txt.trim()) props.onChange(null);
                }}
            />

            {isOpen &&
                createPortal(
                    <AutocompleteDropdown
                        className="autocomplete-dropdown"
                        style={dropdownStyle}
                    >
                        {filteredMenuList.length ? (
                            filteredMenuList.map((option: any) => (
                                <AutocompleteDropdownItem
                                    key={String(getValue(option))}
                                    onClick={() => handleSelect(option)}
                                >
                                    {getLabel(option)}
                                </AutocompleteDropdownItem>
                            ))
                        ) : (
                            <div style={{ padding: 8, textAlign: "center" }}>
                                {props.field.noDataText || "Không tìm thấy kết quả"}
                            </div>
                        )}
                    </AutocompleteDropdown>,
                    document.body
                )}
        </>
    );
}

export interface FormProps {
    validationScope?: string;
    title: string;
    fields: FieldConfigs[];
    validated: boolean;
    requireData?: any;
    mergeFormData?: Function;
    setSubmit?: Function;
    onSubmit?: Function;
    style?: any;
    setFields?: Function;
    // Add cache for UUID resolution
    uuidCache?: Map<string, { text: string; value: string }>;
}

const Form = ({
                  validationScope = "",
                  title,
                  fields,
                  validated,
                  requireData,
                  mergeFormData,
                  onSubmit,
                  setSubmit,
                  style,
                  setFields,
                  uuidCache,
              }: FormProps) => {
    const serverValidation = useContext(ValidationErrorsContext);
    const initialData: Record<string, any> = {};
    fields.forEach((field) => {
        const isProviderForm = title && title.includes("Thông tin nhà cung cấp");
        initialData[field.key] = field.type === "toggle" && !isProviderForm ? true : "";
    });
    const [formData, setFormData] = useState<Record<string, any>>(initialData);
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleChange = (key: string, value: any) => {
        serverValidation.clearError(validationScope + key);
        // Update local state with the new value
        const updatedFormData = { ...formData, [key]: value };
        setFormData(updatedFormData);

        // Call the parent component's merge function with the updated data
        if (mergeFormData) {
            mergeFormData(updatedFormData);
        }

        // Update field metadata if needed
        if (setFields) {
            setFields((prevFields: FieldConfigs[]) =>
                prevFields.map((field: FieldConfigs) =>
                    field.key === key
                        ? { ...field, showPlaceholder: false }
                        : field,
                ),
            );
        }
    };

    const handleBlur = (key: string) => {
        setTouched((prev) => ({ ...prev, [key]: true }));
    };

    useEffect(() => {
        if (requireData) {
            setFormData((prev) => ({ ...prev, ...requireData }));
        }
    }, [requireData]);

    const validateField = (field: FieldConfigs) => {
        const value = formData[field.key];

        const isContactForm = title && title.includes("Thông tin người liên hệ");

        if (isContactForm && field.required && field.key !== "is_default" && !formData["is_default"]) {
            return null;
        }

        if (field.required) {
            if (field.type === "toggle") {
                return null;
            }

            if (value === undefined || value === null || value === "") {
                return "Đây là trường bắt buộc";
            }

            if (typeof value === "string" && value.trim() === "") {
                return "Đây là trường bắt buộc";
            }
        }

        if (field.validation && value) {
            const stringValue = String(value);
            return validate(stringValue, field.validation);
        }

        // Custom validation for new_password and new_password_confirm
        if (
            field.key === "new_password_confirm" &&
            formData["new_password"] !== value
        ) {
            return "Mật khẩu xác nhận không khớp";
        }

        // Custom validation for password and confirm_password
        if (
            field.key === "confirm_password" &&
            formData["password"] !== value
        ) {
            return "Mật khẩu xác nhận không khớp";
        }

        return null;
    };

    useEffect(() => {
        const newErrors: Record<string, string> = {};
        fields.forEach((field) => {
            if (touched[field.key]) {
                const error = validateField(field);
                if (error) {
                    newErrors[field.key] = error;
                }
            }
        });
        setErrors(newErrors);
    }, [fields, formData, touched, requireData]);

    useEffect(() => {
        if (validated) {
            const errorMessage: Record<string, string> = {};
            let isValid = true;
            fields.forEach((field) => {
                const error = validateField(field);
                if (error) {
                    errorMessage[field.key] = error;
                    isValid = false;
                }
                setTouched((prev) => ({ ...prev, [field.key]: true }));
            });
            setErrors(errorMessage);
            if (isValid) {
                onSubmit && onSubmit(formData);
                setSubmit && setSubmit(() => true);
            } else {
                setSubmit && setSubmit(() => false);
            }
        }
    }, [validated]);
    return (
        <ContentWrapper style={style?.contentWrapper}>
            <FormTitle> {title} </FormTitle>
            <FormWrapper style={style?.formWrapper}>
                {fields.map((field) => (
                    <FormRow key={field.key}>
                        <Label>
                            {field.label}
                            {field.required && (
                                <RequiredAsterisk>*</RequiredAsterisk>
                            )}
                        </Label>
                        <InputWrapper>
                            <InputContainer>
                                {field.type === "toggle" && (
                                    <ToggleType
                                        formData={formData}
                                        field={field}
                                        onChange={() =>
                                            handleChange(
                                                field.key,
                                                !formData[field.key],
                                            )
                                        }
                                    />
                                )}

                                {field.type === "text" && (
                                    <TextType
                                        formData={formData}
                                        field={field}
                                        onChange={(e) =>
                                            handleChange(
                                                field.key,
                                                e.target.value,
                                            )
                                        }
                                        onBlur={() => handleBlur(field.key)}
                                        touched={touched}
                                        errors={errors}
                                    />
                                )}

                                {field.type === "quickFills" && (
                                    <QuickFillsType
                                        dataApi={field.dataApi as string}
                                        placeholder={field.placeholder as string}
                                        onChange={(item: any) => handleChange(field.key, item)}
                                        defaultItem={formData[field.key]}
                                        disabled={field.disabled}
                                    />
                                )}

                                {field.type === "dropdown" && field.options && (
                                    <DropdownMenu
                                        menuList={field.options}
                                        menuText={
                                            field.showPlaceholder
                                                ? "Vui lòng chọn"
                                                : field.options.find(opt => opt.value === formData[field.key])?.text || "Vui lòng chọn"
                                        }
                                        handleOnclickItem={(item: any) => {
                                            handleChange(field.key, item.value);
                                        }}
                                        disabled={field.disabled}
                                        style={{
                                            flex: 1,
                                            width: "100%",
                                            height: "36px",
                                            maxWidth: "655px",
                                            padding: "6px 10px",
                                            border: `1px solid ${color.emphasis.NEUTRAL_N70}`,
                                            borderRadius: "5px",
                                            lineHeight: "24px",
                                            fontWeight: "400",
                                            fontSize: "14px",
                                            appearance: "none",
                                            color: formData[field.key]
                                                ? "inherit"
                                                : color.secondary.DARK_GREY,
                                        }}
                                    />
                                )}
                                {field.type === "autocomplete" && (
                                    <Autocomplete
                                        field={field}
                                        value={formData[field.key]}
                                        onChange={(value) => handleChange(field.key, value)}
                                    />
                                )}
                                {field.type === "textanddropdown" && field.options && (
                                    <TextAndDropdownWithSearch
                                        ariaLabel={field.label}
                                        menuList={field.options}
                                        menuText={formData[field.key] || "Vui lòng chọn"}
                                        handleOnclickItem={(item) => {
                                            // Use custom onChange if provided, otherwise use default
                                            if (field.onChange) {
                                                field.onChange(item);
                                            } else {
                                                handleChange(field.key, item.value);
                                            }
                                        }}
                                        onInputChange={(value) => handleChange(field.key, value)}
                                        value={formData[field.key]}
                                        disabled={field.disabled}
                                        fetchItems={field.fetchItems}
                                        uuidCache={uuidCache}
                                        dropdownContainerStyle={field.customProps?.dropdownContainerStyle}
                                    />
                                )}
                                {field.type === "calendar" && (
                                    <CalendarType
                                        formData={formData}
                                        field={field}
                                        onChange={(value) =>
                                            handleChange(field.key, value)
                                        }
                                        onBlur={() => handleBlur(field.key)}
                                        touched={touched}
                                        errors={errors}
                                    />
                                )}
                                {field.type === "custom" && field.customComponent && (
                                    <>
                                        {React.cloneElement(field.customComponent as React.ReactElement, {
                                            ...field.customProps,
                                            formData,
                                            onChange: (value: any) => handleChange(field.key, value),
                                        })}
                                    </>
                                )}

                                {field.type === "datetime" && (
                                    <DatePickerWithTime
                                        clearable={field.customProps?.clearable}
                                        format={field.customProps?.format}
                                        value={formData[field.key] ? dayjs(formData[field.key]).utc() : null}
                                        onChange={(newValue) =>
                                            handleChange(
                                                field.key,
                                                newValue ? dayjs(newValue).valueOf() : null,
                                            )
                                        }
                                        disabled={field.disabled}
                                    />
                                )}
                                {field.type === "multiSelect" && (
                                    <MultiSelectType
                                        formData={formData}
                                        field={field}
                                        onChange={(value) => handleChange(field.key, value)}
                                    />
                                )}
                                {field.type === "search" && (
                                    <SearchDropdown
                                        field={field}
                                        value={formData[field.key]}
                                        onChange={(value) => handleChange(field.key, value)}
                                    />
                                )}
                            </InputContainer>
                            {(serverValidation.errors[validationScope + field.key] || (touched[field.key] && errors[field.key])) && (
                                <ErrorMessage>{serverValidation.errors[validationScope + field.key] || errors[field.key]}</ErrorMessage>
                            )}
                        </InputWrapper>
                    </FormRow>
                ))}
            </FormWrapper>
        </ContentWrapper>
    );
};

export default Form;
